/**
 * Analysis Jobs List Component
 *
 * Shows analysis jobs with status and progress:
 * - pending: Waiting to start
 * - processing: Currently running with progress bar
 * - completed: Finished successfully
 * - failed: Error occurred
 *
 * Polling optimizations:
 * - Stops polling when all jobs are completed/failed
 * - Pauses polling when the browser tab is hidden
 * - Uses useRef for interval to avoid dependency-array thrashing
 */

import { useEffect, useState, useRef, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    PlayCircle,
    Loader2,
    CheckCircle,
    XCircle,
    Download,
    Eye,
    Clock,
    Shield,
    Search,
    ArrowUpDown,
    Pencil,
    Archive,
    ArchiveRestore,
    Tag,
    Ban,
    RotateCcw,
} from 'lucide-react';
import { analysisApi, reportsApi, getErrorMessage } from '../../lib/api';
import toast from 'react-hot-toast';
import AnalysisResultsModal from './AnalysisResultsModal';
import EditMetadataModal from '../common/EditMetadataModal';

interface AnalysisJobsListProps {
    polygonId?: string; // Optional: filter by polygon
    autoRefresh?: boolean; // Auto-refresh for pending/processing jobs
    refreshInterval?: number; // Milliseconds
}

export interface AnalysisJobsListHandle {
    refresh: () => void;
}

export default forwardRef<AnalysisJobsListHandle, AnalysisJobsListProps>(
    function AnalysisJobsList(
        { polygonId, autoRefresh = true, refreshInterval = 5000 },
        ref,
    ) {
        const { t, i18n } = useTranslation();
        const [jobs, setJobs] = useState<any[]>([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);
        const [selectedJob, setSelectedJob] = useState<any | null>(null);

        // Manage controls: search, sort, archive view, inline edit
        const [searchQuery, setSearchQuery] = useState('');
        const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name' | 'status' | 'area'>('newest');
        const [showArchived, setShowArchived] = useState(false);
        const [editingJob, setEditingJob] = useState<any | null>(null);
        const [actioningId, setActioningId] = useState<string | null>(null);

        const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
        const visibleRef = useRef(true);

        const fetchJobs = useCallback(async () => {
            try {
                setError(null);
                const data = await analysisApi.listJobs({
                    page: 1,
                    page_size: 100,
                    polygon_id: polygonId,
                    archived: showArchived,
                });
                setJobs(data.items || []);
            } catch (err: any) {
                console.error('Error loading jobs:', err);
                setError(t('analysis.errorLoadingJobs'));
            } finally {
                setLoading(false);
            }
        }, [polygonId, showArchived, t]);

        // Client-side search + sort over the fetched page
        const displayedJobs = useMemo(() => {
            const q = searchQuery.trim().toLowerCase();
            let list = jobs;
            if (q) {
                list = list.filter((j) =>
                    [j.polygon_name, j.commodity, j.label, j.notes, j.status, j.compliance_status]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase()
                        .includes(q),
                );
            }
            const ts = (d: string) => (d ? new Date(d).getTime() : 0);
            return [...list].sort((a, b) => {
                switch (sortBy) {
                    case 'oldest':
                        return ts(a.created_at) - ts(b.created_at);
                    case 'name':
                        return (a.polygon_name || '').localeCompare(b.polygon_name || '');
                    case 'status':
                        return (a.status || '').localeCompare(b.status || '');
                    case 'area':
                        return (b.area_total_ha || 0) - (a.area_total_ha || 0);
                    case 'newest':
                    default:
                        return ts(b.created_at) - ts(a.created_at);
                }
            });
        }, [jobs, searchQuery, sortBy]);

        const handleEditSave = async ({ label, notes }: { label: string; notes: string }) => {
            if (!editingJob) return;
            try {
                await analysisApi.updateJob(editingJob.id, { label, notes });
                setJobs((prev) =>
                    prev.map((j) => (j.id === editingJob.id ? { ...j, label, notes } : j)),
                );
                toast.success(t('manage.saved'));
            } catch (err: any) {
                toast.error(getErrorMessage(err) || t('manage.actionError'));
                throw err; // keep the modal open on failure
            }
        };

        const handleArchiveToggle = async (job: any) => {
            const next = !job.is_archived;
            const message = next ? t('manage.archiveConfirm') : t('manage.unarchiveConfirm');
            if (!window.confirm(message)) return;
            try {
                setActioningId(job.id);
                await analysisApi.updateJob(job.id, { is_archived: next });
                // The current view shows either active or archived jobs, so the
                // toggled job leaves the visible set either way.
                setJobs((prev) => prev.filter((j) => j.id !== job.id));
                toast.success(next ? t('manage.archivedSuccess') : t('manage.restoredSuccess'));
            } catch (err: any) {
                toast.error(getErrorMessage(err) || t('manage.actionError'));
            } finally {
                setActioningId(null);
            }
        };

        // Cancel a queued/pending job (the backend only allows cancelling jobs
        // that haven't started running yet — this drains a flooded queue).
        const handleCancel = async (job: any) => {
            if (!window.confirm(t('manage.cancelConfirm'))) return;
            try {
                setActioningId(job.id);
                await analysisApi.cancelJob(job.id);
                setJobs((prev) =>
                    prev.map((j) =>
                        j.id === job.id
                            ? { ...j, status: 'cancelled', status_message: t('analysis.cancelledByUser') }
                            : j,
                    ),
                );
                toast.success(t('manage.cancelledSuccess'));
            } catch (err: any) {
                toast.error(getErrorMessage(err) || t('manage.actionError'));
            } finally {
                setActioningId(null);
            }
        };

        // Cancel every not-yet-started job at once (drain a flooded queue).
        const [cancellingAll, setCancellingAll] = useState(false);
        const handleCancelAll = async () => {
            if (!window.confirm(t('manage.cancelAllConfirm'))) return;
            try {
                setCancellingAll(true);
                const { cancelled } = await analysisApi.cancelQueued();
                await fetchJobs();
                toast.success(t('manage.cancelAllSuccess', { count: cancelled }));
            } catch (err: any) {
                toast.error(getErrorMessage(err) || t('manage.actionError'));
            } finally {
                setCancellingAll(false);
            }
        };

        // Retry a failed/cancelled job by re-submitting the same analysis.
        const handleRetry = async (job: any) => {
            try {
                setActioningId(job.id);
                await analysisApi.createJob({
                    polygon_id: job.polygon_id,
                    commodity: job.commodity,
                    baseline_year: job.baseline_year,
                    monitoring_year: job.monitoring_year,
                    generate_report: true,
                    report_language: i18n.language?.startsWith('es') ? 'es' : 'en',
                });
                await fetchJobs();
                toast.success(t('manage.retrySuccess'));
            } catch (err: any) {
                toast.error(getErrorMessage(err) || t('manage.actionError'));
            } finally {
                setActioningId(null);
            }
        };

        // Expose refresh to parent via ref
        useImperativeHandle(ref, () => ({ refresh: fetchJobs }), [fetchJobs]);

        const hasQueuedJobs = jobs.some(
            (j) => j.status === 'pending' || j.status === 'queued',
        );

        // Determine if polling is needed: only when there are active jobs
        const hasActiveJobs = jobs.some(
            (j) => j.status === 'pending' || j.status === 'processing',
        );
        const hasProcessingJob = jobs.some((j) => j.status === 'processing');

        // Manage polling interval with useRef to avoid dependency-array recreation
        useEffect(() => {
            fetchJobs();
        }, [fetchJobs]);

        useEffect(() => {
            // Clear any existing interval
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            // Only poll when: autoRefresh is on, there are active jobs, and tab is visible
            if (!autoRefresh || !hasActiveJobs || !visibleRef.current) return;

            const interval = hasProcessingJob
                ? Math.min(refreshInterval, 3000)
                : refreshInterval;

            intervalRef.current = setInterval(fetchJobs, interval);

            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            };
        }, [autoRefresh, hasActiveJobs, hasProcessingJob, refreshInterval, fetchJobs]);

        // Page Visibility API: pause polling when tab is hidden
        useEffect(() => {
            const handleVisibilityChange = () => {
                visibleRef.current = !document.hidden;

                if (document.hidden) {
                    // Tab hidden → stop polling
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                } else {
                    // Tab visible again → fetch immediately + restart polling if needed
                    fetchJobs();
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () =>
                document.removeEventListener('visibilitychange', handleVisibilityChange);
        }, [fetchJobs]);

        const handleDownloadReport = async (reportId: string) => {
            try {
                const report = await reportsApi.get(reportId);
                const filename = `EUDR_Report_${report.certificate_id}.pdf`;
                await reportsApi.download(reportId, filename);
                toast.success(t('analysis.reportDownloaded'));
            } catch (error: any) {
                console.error('Download error:', error);
                toast.error(t('analysis.reportDownloadError'));
            }
        };

        const getStatusIcon = (status: string) => {
            switch (status) {
                case 'pending':
                    return <Clock className="w-5 h-5 text-gray-400" />;
                case 'processing':
                    return <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />;
                case 'completed':
                    return <CheckCircle className="w-5 h-5 text-green-600" />;
                case 'failed':
                    return <XCircle className="w-5 h-5 text-red-600" />;
                default:
                    return <PlayCircle className="w-5 h-5 text-gray-500" />;
            }
        };

        const dateLocale = i18n.language === 'es' ? 'es-ES' : 'en-US';

        const formatDate = (dateString: string) => {
            return new Date(dateString).toLocaleDateString(dateLocale, {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        };

        const getConfidenceBadge = (job: any) => {
            const confidence = job.results_json?.quality_metrics?.analysis_confidence;
            if (!confidence) return null;

            const level = confidence.toLowerCase();
            const colorMap: Record<string, string> = {
                high: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
                medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
                low: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
            };

            const labelKey = `analysis.confidence.${level}` as const;

            return (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${colorMap[level] || colorMap.medium}`}>
                    <Shield className="w-3 h-3" />
                    {t(labelKey, { defaultValue: confidence })}
                </span>
            );
        };

        if (loading) {
            return (
                <div className="card p-6">
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="card p-6">
                    <p className="text-red-600 text-center">{error}</p>
                    <button onClick={fetchJobs} className="btn-secondary mt-4 mx-auto block">
                        {t('common.retry')}
                    </button>
                </div>
            );
        }

        const toolbar = (
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('manage.searchJobs')}
                        className="input pl-10 w-full"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                            className="input pl-9 pr-8 appearance-none"
                            aria-label={t('manage.sortBy')}
                        >
                            <option value="newest">{t('manage.sortNewest')}</option>
                            <option value="oldest">{t('manage.sortOldest')}</option>
                            <option value="name">{t('manage.sortName')}</option>
                            <option value="status">{t('manage.sortStatus')}</option>
                            <option value="area">{t('manage.sortArea')}</option>
                        </select>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowArchived((v) => !v)}
                        className={`btn-secondary flex items-center gap-2 whitespace-nowrap ${showArchived ? 'ring-2 ring-primary-500' : ''}`}
                    >
                        {showArchived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                        {showArchived ? t('manage.showActive') : t('manage.showArchived')}
                    </button>
                    {hasQueuedJobs && !showArchived && (
                        <button
                            type="button"
                            onClick={handleCancelAll}
                            disabled={cancellingAll}
                            className="btn-secondary flex items-center gap-2 whitespace-nowrap text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                        >
                            {cancellingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                            {t('manage.cancelAll')}
                        </button>
                    )}
                </div>
            </div>
        );

        const emptyState = (
            <div className="card p-8 text-center">
                <PlayCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">
                    {searchQuery.trim()
                        ? t('manage.noResults')
                        : showArchived
                        ? t('manage.noArchived')
                        : t('analysis.noJobs')}
                </p>
            </div>
        );

        return (
            <div className="space-y-4">
                {toolbar}
                {displayedJobs.length === 0 ? emptyState : displayedJobs.map((job) => (
                    <div key={job.id} className="card p-4 sm:p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-start gap-3 min-w-0">
                                {getStatusIcon(job.status)}
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                                        {job.polygon_name || t('nav.polygons')}
                                    </h4>
                                    {job.label && (
                                        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                            <Tag className="w-3 h-3" />
                                            {job.label}
                                        </span>
                                    )}
                                    <p className="text-sm text-gray-500">
                                        {t('polygons.commodity')}: {job.commodity || 'N/A'}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {t('analysis.started')}: {formatDate(job.created_at)}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                                        job.status === 'completed'
                                            ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                            : job.status === 'processing'
                                            ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                            : job.status === 'failed'
                                            ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                            : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                    }`}
                                >
                                    {t(`analysis.status.${job.status}`, { defaultValue: job.status })}
                                </span>
                                {(job.status === 'pending' || job.status === 'queued') && (
                                    <button
                                        type="button"
                                        onClick={() => handleCancel(job)}
                                        disabled={actioningId === job.id}
                                        title={t('manage.cancel')}
                                        aria-label={t('manage.cancel')}
                                        className="p-2 sm:p-1.5 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/30 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                    >
                                        {actioningId === job.id ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Ban className="w-4 h-4" />
                                        )}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setEditingJob(job)}
                                    title={t('manage.editJob')}
                                    aria-label={t('manage.editJob')}
                                    className="p-2 sm:p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleArchiveToggle(job)}
                                    disabled={actioningId === job.id}
                                    title={job.is_archived ? t('manage.unarchive') : t('manage.archive')}
                                    aria-label={job.is_archived ? t('manage.unarchive') : t('manage.archive')}
                                    className="p-2 sm:p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
                                >
                                    {actioningId === job.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : job.is_archived ? (
                                        <ArchiveRestore className="w-4 h-4" />
                                    ) : (
                                        <Archive className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Progress Bar for processing jobs */}
                        {job.status === 'processing' && job.progress !== null && (
                            <div className="mb-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-line dark:border-gray-700">
                                {/* Header: step message + percentage */}
                                <div className="flex items-center gap-2 mb-3">
                                    <Loader2 className="w-4 h-4 text-primary-600 animate-spin flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-1 truncate">
                                        {job.status_message || `${t('analysis.status.processing')}...`}
                                    </span>
                                    <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                                        {job.progress}%
                                    </span>
                                </div>

                                {/* Progress bar */}
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-primary-600 transition-[width] duration-1000 ease-in-out"
                                        style={{ width: `${job.progress}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Analysis Configuration */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">{t('analysis.baselineYear')}</p>
                                <p className="font-medium text-gray-900 dark:text-white">
                                    {job.baseline_year}
                                </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">{t('analysis.monitoringYear')}</p>
                                <p className="font-medium text-gray-900 dark:text-white">
                                    {job.monitoring_year}
                                </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">Compliance</p>
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                            job.compliance_status === 'compliant'
                                                ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                                : job.compliance_status === 'non_compliant'
                                                ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                        }`}
                                    >
                                        {job.compliance_status || t('analysis.status.pending').toLowerCase()}
                                    </span>
                                    {job.status === 'completed' && getConfidenceBadge(job)}
                                </div>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 mb-1">{t('analysis.report')}</p>
                                <p className="font-medium text-gray-900 dark:text-white">
                                    {job.report_id ? t('analysis.generated') : t('common.no')}
                                </p>
                            </div>
                        </div>

                        {/* Actions */}
                        {job.status === 'completed' && (
                            <div className="flex items-center gap-2 pt-4 border-t border-gray-100 dark:border-gray-700">
                                {job.report_id && (
                                    <button
                                        onClick={() => handleDownloadReport(job.report_id)}
                                        className="btn-primary text-sm flex items-center gap-2"
                                    >
                                        <Download className="w-4 h-4" />
                                        {t('analysis.downloadReport')}
                                    </button>
                                )}
                                <button
                                    onClick={() => setSelectedJob(job)}
                                    className="btn-secondary text-sm flex items-center gap-2"
                                >
                                    <Eye className="w-4 h-4" />
                                    {t('analysis.viewResults')}
                                </button>
                            </div>
                        )}

                        {/* Error Message */}
                        {job.status === 'failed' && (
                            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm text-red-800 dark:text-red-200">
                                            <strong>{t('analysis.analysisFailed')}</strong>
                                            {job.error_message ? ` — ${job.error_message}` : ''}
                                        </p>
                                        <p className="text-xs text-red-700/80 dark:text-red-300/80 mt-1">
                                            {t('analysis.failedHint')}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRetry(job)}
                                        disabled={actioningId === job.id}
                                        className="btn-secondary flex items-center gap-2 whitespace-nowrap text-sm disabled:opacity-50"
                                    >
                                        {actioningId === job.id ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <RotateCcw className="w-4 h-4" />
                                        )}
                                        {t('manage.retry')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {/* Results Modal */}
                {selectedJob && (
                    <AnalysisResultsModal
                        job={selectedJob}
                        onClose={() => setSelectedJob(null)}
                    />
                )}

                {/* Edit metadata modal */}
                {editingJob && (
                    <EditMetadataModal
                        title={t('manage.editJob')}
                        subtitle={editingJob.polygon_name || undefined}
                        initialLabel={editingJob.label}
                        initialNotes={editingJob.notes}
                        onSave={handleEditSave}
                        onClose={() => setEditingJob(null)}
                    />
                )}
            </div>
        );
    },
);
